import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  AssignedLawyer,
  CreatedLegalRequest,
  LawyerOption,
  LegalRequestDetailResult,
  LegalRequestRow
} from './legalRequestTypes';

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const envelope = (response: unknown): Record<string, unknown> => asRecord(asRecord(response).data);

const asId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

const asNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asAssignedLawyer = (value: unknown): AssignedLawyer | null => {
  const raw = asNullableRecord(value);
  return raw === null ? null : { id: asId(raw.id) ?? 0, name: asString(raw.name) };
};

export function normalizeLegalRequestList(response: unknown): LegalRequestRow[] {
  const data = asRecord(response).data;
  const items = Array.isArray(data) ? data : asRecord(data).items;
  return Array.isArray(items) ? items.map(asRecord) : [];
}

export function normalizeCreatedLegalRequest(response: unknown): CreatedLegalRequest {
  return { id: asId(envelope(response).id) };
}

export function normalizeLegalRequestDetail(response: unknown): LegalRequestDetailResult {
  const data = envelope(response);
  return {
    legalRequest: asNullableRecord(data.legalRequest),
    property: asNullableRecord(data.property),
    assignedLawyer: asAssignedLawyer(data.assignedLawyer),
    preferredLawyer: asAssignedLawyer(data.preferredLawyer)
  };
}

export function normalizeLawyerOptions(response: unknown): LawyerOption[] {
  const data = asRecord(response).data;
  const items = Array.isArray(data) ? data : asRecord(data).items;
  return Array.isArray(items)
    ? items.map((item) => {
        const row = asRecord(item);
        return {
          id: asId(row.id) ?? 0,
          name: asString(row.name),
          specialization: asNullableString(row.specialization),
          experience: asNumberOrNull(row.experience),
          barCouncil: asNullableString(row.bar_council)
        };
      })
    : [];
}

export const legalRequestApi = createApi({
  reducerPath: 'legalRequestApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['LegalRequest'],
  endpoints: (builder) => ({
    createLegalRequest: builder.mutation<
      CreatedLegalRequest,
      { id: number; preferredLawyerId?: number }
    >({
      query: ({ id, preferredLawyerId }) => ({
        url: `buyer/properties/${id}/legal-request`,
        method: 'POST',
        body: preferredLawyerId === undefined ? {} : { preferredLawyerId }
      }),
      transformResponse: (response: unknown) => normalizeCreatedLegalRequest(response),
      invalidatesTags: [{ type: 'LegalRequest', id: 'LIST' }]
    }),
    listLegalRequests: builder.query<LegalRequestRow[], void>({
      query: () => ({ url: 'buyer/legal-requests' }),
      transformResponse: (response: unknown) => normalizeLegalRequestList(response),
      providesTags: [{ type: 'LegalRequest', id: 'LIST' }]
    }),
    getLegalRequest: builder.query<LegalRequestDetailResult, { id: number }>({
      query: ({ id }) => ({ url: `buyer/legal-requests/${id}` }),
      transformResponse: (response: unknown) => normalizeLegalRequestDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'LegalRequest', id: String(arg.id) }]
    }),
    listAvailableLawyers: builder.query<LawyerOption[], void>({
      query: () => ({ url: 'buyer/lawyers' }),
      transformResponse: (response: unknown) => normalizeLawyerOptions(response)
    })
  })
});

export const {
  useCreateLegalRequestMutation,
  useListLegalRequestsQuery,
  useGetLegalRequestQuery,
  useListAvailableLawyersQuery
} = legalRequestApi;
